// Pipeline velocity tile — FE-SCRM-058 (BE-SCRM-027).
//
// Three sub-views inside the Card:
//
// 1. Headline numbers row — pipeline velocity total + overall win rate
//    (averaged across producer dimension) + count of stale rows.
// 2. Stage-conversion-rate table — for each open stage, the % of
//    opportunities that transitioned to the next stage (vs lost).
// 3. Stale opportunities list — top 10 with longest time since last
//    activity.
//
// Window selector (90 / 180 / 365) sits in the card header. The BE
// clamps to [7, 365]; 90 is the default per the report endpoint.

import { useState } from "react";
import { Link } from "react-router-dom";

import {
  Badge, Card, SegmentedControl, Table, TBody, THead, Th, Td, Tr,
} from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { useVelocityReportQuery } from "./hooks";
import {
  STAGE_LABEL, VELOCITY_WINDOWS,
  formatCents, formatDays, formatPercent,
} from "./labels";
import type { VelocityResponse } from "./types";


// Open-stage order for the conversion table. Closed_won / closed_lost
// are not "from" stages here — they're outcomes.
const OPEN_STAGES_ORDER = [
  "lead",
  "meeting_set",
  "qualified",
  "proposal",
  "negotiation",
  "bor_signed_pending_effective_date",
];


export function PipelineVelocityTile() {
  const [windowDays, setWindowDays] = useState<number>(90);
  const query = useVelocityReportQuery(windowDays);

  return (
    <Card
      data-testid="pipeline-velocity-tile"
      data-section="pipeline-velocity"
      className="flex flex-col gap-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-md font-semibold text-text-primary">
            Pipeline velocity
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            How fast pursuits move through the pipeline + which stages
            stall + which producers convert.
          </p>
        </div>
        <SegmentedControl
          label="Window"
          value={String(windowDays)}
          options={VELOCITY_WINDOWS.map((w) => ({
            value: String(w.value),
            label: w.label,
          }))}
          onChange={(v) => setWindowDays(Number.parseInt(v, 10))}
          testIdPrefix="velocity-window"
          className="!flex-row items-center"
        />
      </header>

      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={3} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load pipeline velocity"
          retry={() => query.refetch()}
        />
      ) : query.data ? (
        <VelocityBody data={query.data} />
      ) : null}
    </Card>
  );
}


function VelocityBody({ data }: { data: VelocityResponse }) {
  const isEmpty = (
    Object.keys(data.avg_days_in_stage).length === 0
    && Object.keys(data.stage_conversion_rates).length === 0
    && data.stale_opportunities.length === 0
    && data.pipeline_velocity_total === 0
  );
  if (isEmpty) {
    return (
      <div data-testid="velocity-empty-state">
        <DashboardEmptyState
          title="Not enough movement yet"
          description={
            "Once opportunities transition between stages or close, "
            + "velocity numbers and stale-pursuit flags will appear here."
          }
        />
      </div>
    );
  }

  const overallWinRate = averageWinRate(data.win_rate_by.producer_id);

  return (
    <div className="flex flex-col gap-4">
      <HeadlineRow
        velocityTotal={data.pipeline_velocity_total}
        overallWinRate={overallWinRate}
        staleCount={data.stale_opportunities.length}
      />
      <StageConversionTable
        avgDays={data.avg_days_in_stage}
        rates={data.stage_conversion_rates}
      />
      <StaleOpportunitiesList rows={data.stale_opportunities} />
    </div>
  );
}


function HeadlineRow({
  velocityTotal, overallWinRate, staleCount,
}: {
  velocityTotal: number;
  overallWinRate: number | null;
  staleCount: number;
}) {
  return (
    <div
      data-testid="velocity-headline-row"
      className="grid grid-cols-3 gap-3"
    >
      <Headline
        label="Pipeline velocity"
        value={formatCents(Math.round(velocityTotal))}
        hint="Dollars per day, weighted by stage probability."
      />
      <Headline
        label="Win rate"
        value={formatPercent(overallWinRate)}
        hint="Across closed opportunities in the window."
      />
      <Headline
        label="Stale opportunities"
        value={String(staleCount)}
        hint="Open pursuits with 14+ days of no completed activity."
      />
    </div>
  );
}


function Headline({
  label, value, hint,
}: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-md border border-border-subtle bg-raised p-3">
      <p className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold text-text-primary">
        {value}
      </p>
      <p className="mt-1 text-xs text-text-secondary">{hint}</p>
    </div>
  );
}


function StageConversionTable({
  avgDays, rates,
}: {
  avgDays: Record<string, number>;
  rates: Record<string, Record<string, number>>;
}) {
  return (
    <div
      data-testid="velocity-stage-conversion"
      className="overflow-hidden rounded-md border border-border-subtle"
    >
      <Table density="xs" cellPadding="tight">
        <THead className="bg-recessed text-text-tertiary">
          <Tr>
            <Th>Stage</Th>
            <Th>Avg days</Th>
            <Th>Advances</Th>
            <Th>Loses</Th>
          </Tr>
        </THead>
        <TBody>
          {OPEN_STAGES_ORDER.map((stage, idx) => {
            const next = OPEN_STAGES_ORDER[idx + 1];
            const stageRates = rates[stage] ?? {};
            const advances = next ? stageRates[next] : stageRates.closed_won;
            const lost = stageRates.closed_lost ?? 0;
            return (
              <Tr
                key={stage}
                data-testid={`velocity-stage-row-${stage}`}
                className="border-t border-border-subtle"
              >
                <Td className="text-text-primary">
                  {STAGE_LABEL[stage] ?? stage}
                </Td>
                <Td className="text-text-secondary">
                  {formatDays(avgDays[stage] ?? null)}
                </Td>
                <Td className="text-text-secondary">
                  {advances !== undefined ? formatPercent(advances) : "—"}
                </Td>
                <Td className="text-text-secondary">
                  {lost > 0 ? formatPercent(lost) : "—"}
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}


function StaleOpportunitiesList({
  rows,
}: { rows: VelocityResponse["stale_opportunities"] }) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md bg-recessed p-3 text-xs text-text-secondary">
        Every open pursuit has had activity in the last 14 days.
      </div>
    );
  }
  const top = rows.slice(0, 10);
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium text-text-secondary">
        Stale opportunities (top {top.length})
      </p>
      <ol
        data-testid="velocity-stale-list"
        className="flex flex-col gap-1.5"
      >
        {top.map((row) => (
          <li
            key={row.opportunity_id}
            data-testid={`velocity-stale-row-${row.opportunity_id}`}
            className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-raised px-3 py-2 text-xs"
          >
            <Link
              to={`/dashboard/sales-crm/opportunities/${encodeURIComponent(row.opportunity_id)}`}
              className="text-text-primary hover:underline"
            >
              {STAGE_LABEL[row.stage] ?? row.stage}
            </Link>
            <Badge variant="regulatory">
              {formatDays(row.days_since_last_activity)}
            </Badge>
          </li>
        ))}
      </ol>
    </div>
  );
}


function averageWinRate(byProducer: Record<string, number>): number | null {
  const values = Object.values(byProducer);
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}
